/**
 * Navigation-app deep links.
 *
 * Each provider returns a URL string given {lat, lng, address?, label?}.
 *
 * Precision rule (May 2026): if a real street address is available we
 * pass it as the destination instead of the lat/lng pair. All three
 * apps' routing engines snap a street address to the actual building
 * entrance / driveway, while passing lat/lng routes literally to the
 * dropped pin — which is often off by ~half a block on parking lots
 * and centroid-style pins. Real user report: *"When you click
 * directions it doesn't necessarily go to the address on the event
 * card. It's close, but not precise."* Solved by routing to text.
 *
 * lat/lng is kept as a fallback for events that never got a real
 * address (impromptu / manual pin only) and as a safety net for
 * apps that don't recognise the address string.
 */
const PROVIDERS = {
  google: {
    key: "google",
    label: "Google Maps",
    // Universal Maps URL. `destination` accepts either a "lat,lng"
    // pair OR a free-form text address. Google's address geocoder
    // snaps to the actual storefront / driveway entrance.
    href: ({ lat, lng, address }) => {
      const dest = address
        ? encodeURIComponent(address)
        : `${lat},${lng}`;
      return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    },
  },
  apple: {
    key: "apple",
    label: "Apple Maps",
    // `daddr` accepts a coord, a coord+name, or a free-form address.
    // `dirflg=d` = drive. We append a `q=` query as belt-and-braces
    // for older iOS versions that ignore raw address in daddr.
    href: ({ lat, lng, address, label }) => {
      if (address) {
        const a = encodeURIComponent(address);
        return `https://maps.apple.com/?daddr=${a}&dirflg=d&q=${a}`;
      }
      const dest = label
        ? `${lat},${lng} (${encodeURIComponent(label)})`
        : `${lat},${lng}`;
      return `https://maps.apple.com/?daddr=${dest}&dirflg=d`;
    },
  },
  waze: {
    key: "waze",
    label: "Waze",
    // Waze's `q=` accepts a free-form address; falls back to `ll=`
    // (lat,lng) when we don't have one. `navigate=yes` auto-starts
    // the route on the mobile app and gracefully fails over to web.
    href: ({ lat, lng, address }) => {
      if (address) {
        return `https://www.waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
      }
      return `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    },
  },
};

const STORAGE_KEY = "tt:directions:default-provider";

export function getStoredDirectionsProvider() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && PROVIDERS[v]) return v;
  } catch {
    /* localStorage disabled — fall through to default */
  }
  return null;
}

export function setStoredDirectionsProvider(key) {
  if (!PROVIDERS[key]) return;
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* ignore */
  }
}

export function getDirectionsProviders() {
  return Object.values(PROVIDERS);
}

export function buildDirectionsUrl(providerKey, { lat, lng, address, label } = {}) {
  const p = PROVIDERS[providerKey] || PROVIDERS.google;
  return p.href({ lat, lng, address, label });
}

/**
 * Default-pick heuristic — only used the first time before the user
 * picks anything. Apple Maps for iOS / iPadOS / Macs, Google for
 * everything else. We deliberately don't auto-pick Waze (most users
 * don't have it installed; the link would 404 the web fallback).
 */
export function guessDefaultProvider() {
  try {
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";
    const isAppleMobile = /iPhone|iPad|iPod/.test(ua);
    const isMac = /Mac/.test(platform) && !/iPad/.test(ua);
    if (isAppleMobile || isMac) return "apple";
  } catch {
    /* SSR or restricted env */
  }
  return "google";
}

export function effectiveDefaultProvider() {
  return getStoredDirectionsProvider() || guessDefaultProvider();
}
